import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import ListIcon from "@mui/icons-material/List";
import GroupsIcon from "@mui/icons-material/Groups";
import SearchIcon from "@mui/icons-material/Search";
import ArticleIcon from "@mui/icons-material/Article";
import SettingsIcon from "@mui/icons-material/Settings";
import FlagCircleIcon from "@mui/icons-material/FlagCircle";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import { useNavigate } from "react-router-dom";
import { selectFirstTeamReport } from "../../../../../slices/teamReportsSlice";
import { useSelector, useDispatch } from "react-redux";
import { selectTotalTeamMortgages } from "../../../../../slices/teamMortgagesSlice";
import { selectTotalTeamInvestigations } from "../../../../../slices/teamActiveLeadsSlice";
import { useRemoveReportNotifyUserMutation } from "../../../../../slices/api/apiSlice";
import {
  selectAppLoaded,
  selectNavSelection,
  setNavSelection,
} from "../../../../../slices/sessionDataSlice";
import {
  removeMortgageNotifcationCount,
  removeNewLeadNotification,
  removeReportNotifcationCount,
  selectMortgageNotifCount,
  selectNewAwaitingActionCount,
  selectNewReportRemoved,
  selectTotalLeadNotifCount,
  selectUserNewReports,
  setNewReportRemoved,
} from "../../../../../slices/userNotificationsSlice";

const Navigation = ({ userId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const appLoaded = useSelector(selectAppLoaded);
  const newReports = useSelector(selectUserNewReports);
  const firstReport = useSelector(selectFirstTeamReport);
  const navSelection = useSelector(selectNavSelection);
  const newReportRemoved = useSelector(selectNewReportRemoved);
  const mortgageNotifCount = useSelector(selectMortgageNotifCount);
  const totalTeamMortgages = useSelector(selectTotalTeamMortgages);
  const totalLeadNotifCount = useSelector(selectTotalLeadNotifCount);
  const newAwaitingActionCount = useSelector(selectNewAwaitingActionCount);
  const totalTeamInvestigations = useSelector(selectTotalTeamInvestigations);

  const [removeUserNotification] = useRemoveReportNotifyUserMutation();

  const [lastOverview, setLastOverview] = useState("leadGeneration");
  const [reportsSelected, setReportsSelected] = useState(false);

  const changeSelection = (remember, content) => {
    if (remember) {
      if (content === "refinancesOverview") {
        setLastOverview("refinance");
      } else if (content === "renegotiationsOverview") {
        setLastOverview("renegotiation");
      }
    }

    if (content === "investigations") {
      dispatch(
        removeNewLeadNotification({
          status: "awaitingAction",
          count: newAwaitingActionCount,
        })
      );
    }

    if (content === "mortgageRepository") {
      dispatch(removeMortgageNotifcationCount());
    }

    navigate(`/dashboard/${content}`);
    dispatch(setNavSelection(content));

    if (reportsSelected) {
      if (firstReport) {
        const notifyUser = firstReport.notifyUser;

        if (notifyUser && !newReportRemoved) {
          removeUserNotification({
            userId,
            reportId: firstReport._id,
          });
          dispatch(setNewReportRemoved(true));
        }

        setReportsSelected(false);
      }
    }

    if (content === "reports") {
      setReportsSelected(true);
      dispatch(removeReportNotifcationCount());
    }
  };

  const handleShowOverview = () => {
    if (lastOverview === "refinance") {
      changeSelection(true, "refinancesOverview");
    } else if (lastOverview === "renegotiation") {
      changeSelection(true, "renegotiationsOverview");
    } else {
      changeSelection(true, "leadGeneration");
    }
  };

  const isOverviewSelected =
    navSelection === "refinancesOverview" ||
    navSelection === "renegotiationsOverview" ||
    navSelection === "leadGeneration";

  const renderNavItem = ({
    id,
    label,
    icon,
    count,
    notificationCount,
    onClick,
    disabled,
    active,
  }) => (
    <NavItem
      type="button"
      className={active ? "active" : ""}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
    >
      {notificationCount > 0 && <NavBadge>{notificationCount}</NavBadge>}

      <NavIcon className={notificationCount > 0 ? "hasBadge" : ""}>
        {icon}
      </NavIcon>

      <NavLabel className={notificationCount > 0 ? "hasBadge" : ""}>
        {label}
        {count !== undefined && <CountText>({count})</CountText>}
      </NavLabel>
    </NavItem>
  );

  const renderSubItem = ({ id, numeral, label }) => (
    <SubNavItem
      type="button"
      className={navSelection === id ? "active" : ""}
      disabled={navSelection === id || !appLoaded}
      onClick={
        navSelection === id || !appLoaded
          ? undefined
          : () => changeSelection(true, id)
      }
    >
      <SubIndex>{numeral}</SubIndex>
      <span>{label}</span>
    </SubNavItem>
  );

  return (
    <NavigationShell>
      <NavHeader>
        <HeaderEyebrow>Workspace</HeaderEyebrow>
        <HeaderTitle>Navigation</HeaderTitle>
      </NavHeader>

      <NavList>
        {renderNavItem({
          id: "investigations",
          label:
            totalTeamInvestigations === 1
              ? "Active Investigation"
              : "Active Investigations",
          count: totalTeamInvestigations,
          notificationCount: totalLeadNotifCount,
          icon: <FlagCircleIcon />,
          active: navSelection === "investigations",
          disabled: navSelection === "investigations" || !appLoaded,
          onClick: () => changeSelection(false, "investigations"),
        })}

        {renderNavItem({
          id: "teamPerformance",
          label: "Team Performance",
          icon: <QueryStatsIcon />,
          active: isOverviewSelected,
          disabled: isOverviewSelected || !appLoaded,
          onClick: handleShowOverview,
        })}

        <SubNavGroup>
          {renderSubItem({
            id: "leadGeneration",
            numeral: "i.",
            label: "Lead Generation",
          })}
          {renderSubItem({
            id: "renegotiationsOverview",
            numeral: "ii.",
            label: "Renegotiations",
          })}
          {renderSubItem({
            id: "refinancesOverview",
            numeral: "iii.",
            label: "Refinances",
          })}
        </SubNavGroup>

        {renderNavItem({
          id: "mortgageRepository",
          label: "Mortgage Repository",
          count: totalTeamMortgages,
          notificationCount: mortgageNotifCount,
          icon: <ListIcon />,
          active: navSelection === "mortgageRepository",
          disabled: navSelection === "mortgageRepository" || !appLoaded,
          onClick: () => changeSelection(true, "mortgageRepository"),
        })}

        {renderNavItem({
          id: "portfolioFinancials",
          label: "Portfolio Financials",
          icon: <AttachMoneyIcon />,
          active: navSelection === "portfolioFinancials",
          disabled: navSelection === "portfolioFinancials" || !appLoaded,
          onClick: () => changeSelection(false, "portfolioFinancials"),
        })}

        {renderNavItem({
          id: "memberPerformance",
          label: "Member Performance",
          icon: <GroupsIcon />,
          active: navSelection === "memberPerformance",
          disabled: navSelection === "memberPerformance" || !appLoaded,
          onClick: () => changeSelection(false, "memberPerformance"),
        })}

        {renderNavItem({
          id: "configuration",
          label: "System Configuration",
          icon: <BuildCircleIcon />,
          active: navSelection === "configuration",
          disabled: navSelection === "configuration" || !appLoaded,
          onClick: () => changeSelection(false, "configuration"),
        })}

        {renderNavItem({
          id: "propertySearch",
          label: "Property Search",
          icon: <SearchIcon />,
          active: navSelection === "propertySearch",
          disabled: navSelection === "propertySearch" || !appLoaded,
          onClick: () => changeSelection(false, "propertySearch"),
        })}

        {renderNavItem({
          id: "reports",
          label: "Closure Reports",
          notificationCount: newReports,
          icon: <ArticleIcon />,
          active: navSelection === "reports",
          disabled: navSelection === "reports" || !appLoaded,
          onClick: () => changeSelection(false, "reports"),
        })}

        {renderNavItem({
          id: "settings",
          label: "Settings",
          icon: <SettingsIcon />,
          active: navSelection === "settings",
          disabled: navSelection === "settings" || !appLoaded,
          onClick: () => changeSelection(false, "settings"),
        })}

        {renderNavItem({
          id: "guide",
          label: "Information Guide",
          icon: <AutoStoriesIcon />,
          active: navSelection === "guide",
          disabled: navSelection === "guide" || !appLoaded,
          onClick: () => changeSelection(false, "guide"),
        })}
      </NavList>
    </NavigationShell>
  );
};

const NavigationShell = styled("nav")({
  gridRow: 2,
  gridColumn: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  padding: "1rem 0.8rem",
  overflowY: "auto",
  overflowX: "hidden",

  "::-webkit-scrollbar": {
    width: "0.35rem",
  },

  "::-webkit-scrollbar-thumb": {
    borderRadius: "999px",
    background: "rgba(148,163,184,0.35)",
  },
});

const NavHeader = styled("div")({
  padding: "0.2rem 0.6rem 0.95rem 0.6rem",
});

const NavList = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "0.15rem",
});

const NavLabel = styled("div")({
  gridColumn: 2,
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: "0.3rem",
  color: "currentColor",
  fontSize: "1rem",
  fontWeight: 850,
  lineHeight: 1.1,

  "&.hasBadge": {
    transform: "translateX(-0.75rem)",
  },
});

const SubIndex = styled("span")({
  width: "1.8rem",
  color: "inherit",
  fontSize: "0.82rem",
  fontWeight: 950,
  textTransform: "uppercase",
});

const HeaderEyebrow = styled("div")({
  color: "#64748b",
  fontSize: "0.78rem",
  fontWeight: 900,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
});

const HeaderTitle = styled("div")({
  color: "#1f2937",
  fontSize: "1.45rem",
  fontWeight: 950,
  letterSpacing: 0,
});

const NavItem = styled("button")({
  position: "relative",
  width: "100%",
  minHeight: "3rem",
  display: "grid",
  gridTemplateColumns: "3.2rem minmax(0, 1fr)",
  alignItems: "center",
  border: "0.1rem solid transparent",
  borderRadius: "0.8rem",
  padding: "0 0.7rem",
  color: "#475569",
  background: "transparent",
  cursor: "pointer",
  textAlign: "left",
  transition:
    "background 160ms ease, border-color 160ms ease, color 160ms ease, transform 160ms ease, box-shadow 160ms ease",

  "&:hover:not(:disabled)": {
    color: "#1f2937",
    background: "rgba(255,255,255,0.52)",
    borderColor: "rgba(120,139,158,0.24)",
    transform: "translateX(0.15rem)",
  },

  "&.active": {
    color: "#1f2937",
    background:
      "linear-gradient(90deg, rgba(37,99,235,0.14), rgba(255,255,255,0.58))",
    borderColor: "rgba(59,130,246,0.26)",
    boxShadow:
      "inset 0.25rem 0 0 #2563eb, 0 0.65rem 1.2rem rgba(71,85,105,0.08)",
  },

  "&:disabled": {
    cursor: "default",
  },
});

const NavIcon = styled("div")({
  gridColumn: 1,
  width: "2.5rem",
  height: "2.5rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#2563eb",

  "&.hasBadge": {
    transform: "translateX(-0.75rem)",
  },

  "& svg": {
    width: "2rem",
    height: "2rem",
  },
});

const CountText = styled("span")({
  color: "#64748b",
  fontSize: "0.9rem",
  fontWeight: 900,
});

const NavBadge = styled("div")({
  position: "absolute",
  left: "0.55rem",
  top: "0.45rem",
  minWidth: "1.7rem",
  height: "1.7rem",
  padding: "0 0.45rem",
  borderRadius: "999px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  background: "#dc2626",
  border: "0.15rem solid #eef3f8",
  fontSize: "0.85rem",
  fontWeight: 950,
  zIndex: 2,
});

const SubNavGroup = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "0.2rem",
  margin: "-0.1rem 0 0.35rem 3rem",
  padding: "0.2rem 0 0.2rem 0.8rem",
  borderLeft: "0.1rem solid rgba(120,139,158,0.28)",
});

const SubNavItem = styled("button")({
  width: "100%",
  height: "2.7rem",
  display: "flex",
  alignItems: "center",
  gap: "0.45rem",
  borderRadius: "0.75rem",
  border: "0.1rem solid transparent",
  padding: "0 0.7rem",
  color: "#64748b",
  background: "transparent",
  fontSize: "0.92rem",
  fontWeight: 800,
  cursor: "pointer",
  textAlign: "left",
  transition: "background 160ms ease, color 160ms ease",

  "&:hover:not(:disabled)": {
    color: "#1f2937",
    background: "rgba(255,255,255,0.48)",
    borderColor: "rgba(120,139,158,0.18)",
  },

  "&.active": {
    color: "#2563eb",
    background: "rgba(59,130,246,0.12)",
    borderColor: "rgba(59,130,246,0.18)",
  },

  "&:disabled": {
    cursor: "default",
  },
});

export default Navigation;